import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { ColorModeProvider } from './contexts/ColorModeContext';
import { AppProvider } from './contexts/AppContext';
import { useApp } from './contexts/AppContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import QrLoginPage from './pages/QrLoginPage';
import ApproveLoginPage from './pages/ApproveLoginPage';
import SecurityPage from './pages/SecurityPage';
import DashboardPage from './pages/DashboardPage';
import MachinesPage from './pages/MachinesPage';
import JobsPage from './pages/JobsPage';
import WorklistsPage from './pages/WorklistsPage';
import MessagesPage from './pages/MessagesPage';
import ReportsPage from './pages/ReportsPage';
import AdminPage from './pages/AdminPage';
import MachinesManagementPage from './pages/MachinesManagementPage';
import MachineDetailPage from './pages/MachineDetailPage';
import Layout from './components/Layout';
import { canAccessAdminPanel } from './utils/permissions';
import CustomersManagementPage from './pages/CustomersManagementPage';
import ResellersManagementPage from './pages/ResellersManagementPage';
import DatabaseManagementPage from './pages/DatabaseManagementPage';
import ActivationPage from './pages/ActivationPage';
import HmiUpdatesPage from './pages/HmiUpdatesPage';
import SoftwareUpdatesPage from './pages/SoftwareUpdatesPage';
import RemoteAssistancePage from './pages/RemoteAssistancePage';
import SoftwareLicensesPage from './pages/SoftwareLicensesPage';
import NextManagerLicensesPage from './pages/NextManagerLicensesPage';
import MyComputersPage from './pages/MyComputersPage';
import NotFoundPage from './pages/NotFoundPage';
import { getReturnPath } from './utils/navigation';

const LoginRoute = ({ children }: { children: React.ReactNode }) => {
  const { state } = useApp();
  const location = useLocation();
  return state.auth.isAuthenticated ? <Navigate to={getReturnPath(location.state)} replace /> : <>{children}</>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useApp();
  const location = useLocation();

  if (state.auth.loading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress aria-label="Caricamento della sessione" /></Box>;
  }

  if (!state.auth.isAuthenticated) {
    return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { state } = useApp();
  const [initialized, setInitialized] = useState(false);
  useEffect(() => { if (!state.auth.loading) setInitialized(true); }, [state.auth.loading]);

  if (state.auth.loading && !initialized) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress aria-label="Caricamento della piattaforma" />
    </Box>;
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginRoute><LoginPage /></LoginRoute>} />
        <Route path="/login/qr" element={<LoginRoute><QrLoginPage /></LoginRoute>} />
        <Route path="/register" element={state.auth.isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/approve" element={
          <ProtectedRoute>
            <ApproveLoginPage />
          </ProtectedRoute>
        } />
        <Route path="/security" element={
          <ProtectedRoute>
            <Layout>
              <SecurityPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/licenses" element={<ProtectedRoute><Layout><SoftwareLicensesPage /></Layout></ProtectedRoute>} />
        <Route path="/licenses/next-manager" element={<ProtectedRoute><Layout><NextManagerLicensesPage /></Layout></ProtectedRoute>} />
        <Route path="/software-updates" element={<ProtectedRoute><Layout><SoftwareUpdatesPage /></Layout></ProtectedRoute>} />
        <Route path="/my-computers" element={
          <ProtectedRoute><Layout><MyComputersPage /></Layout></ProtectedRoute>
        } />
        <Route path="/remote-assistance" element={
          <ProtectedRoute>
            <Layout><RemoteAssistancePage /></Layout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/machines" element={
          <ProtectedRoute>
            <Layout>
              <MachinesPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/machines/:id" element={
          <ProtectedRoute>
            <Layout>
              <MachineDetailPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/jobs" element={
          <ProtectedRoute>
            <Layout>
              <JobsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/worklists" element={
          <ProtectedRoute>
            <Layout>
              <WorklistsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/messages" element={
          <ProtectedRoute>
            <Layout>
              <MessagesPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute>
            <Layout>
              <ReportsPage />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            {state.auth.user && canAccessAdminPanel(state.auth.user) ? (
              <Layout>
                <AdminPage />
              </Layout>
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        } />
        <Route path="/machines/manage" element={
          <ProtectedRoute>
            {state.auth.user && (state.auth.user.role === 'administrator' || state.auth.user.role === 'reseller') ? (
              <Layout>
                <MachinesManagementPage />
              </Layout>
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        } />
        <Route path="/admin/customers" element={
          <ProtectedRoute>
            {state.auth.user && (state.auth.user.role === 'administrator' || state.auth.user.role === 'reseller') ? (
              <Layout>
                <CustomersManagementPage />
              </Layout>
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        } />
        <Route path="/admin/resellers" element={
          <ProtectedRoute>
            {state.auth.user && state.auth.user.role === 'administrator' ? (
              <Layout>
                <ResellersManagementPage />
              </Layout>
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        } />
        <Route path="/reseller/clients" element={
          <ProtectedRoute>
            {state.auth.user && (state.auth.user.role === 'administrator' || state.auth.user.role === 'reseller') ? (
              <Layout>
                <CustomersManagementPage />
              </Layout>
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        } />
        <Route path="/admin/database" element={
          <ProtectedRoute>
            {state.auth.user && state.auth.user.role === 'administrator' ? (
              <Layout>
                <DatabaseManagementPage />
              </Layout>
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        } />
        <Route path="/admin/activation" element={
          <ProtectedRoute>
            {state.auth.user && state.auth.user.role === 'administrator' ? (
              <Layout>
                <ActivationPage />
              </Layout>
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        } />
        <Route path="/admin/hmi-updates" element={
          <ProtectedRoute>
            {state.auth.user && state.auth.user.role === 'administrator' ? (
              <Layout>
                <HmiUpdatesPage />
              </Layout>
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        } />
        <Route path="*" element={<ProtectedRoute><Layout><NotFoundPage /></Layout></ProtectedRoute>} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ColorModeProvider>
      <AppProvider>
        <AppRoutes />
      </AppProvider>
    </ColorModeProvider>
  );
};

export default App;
